"use client";

import React from "react";
import Link from "next/link";
import { NewButton } from "@/components/ui/NewButton";
import { useIsAdLanding } from "@/lib/hooks/useIsAdLanding";

const inlineCtaBg: Record<"emerald" | "gray" | "white", string> = {
  emerald: "bg-emerald-50",
  gray: "bg-gray-100",
  white: "bg-white",
};

/** Challenges-section dual CTA — hides Start Free Trial on Google Ads sessions. */
export function OfficeChallengesCta({ trialHref }: { trialHref: string }) {
  const isAdLanding = useIsAdLanding();

  return (
    <div className="mt-8 flex flex-wrap justify-center gap-4">
      <Link href="/book-a-demo">
        <NewButton variant="primary" size="md">
          Book a Free Demo
        </NewButton>
      </Link>
      {!isAdLanding && (
        <Link href={trialHref} target="_blank" rel="noopener noreferrer">
          <NewButton variant="outlinePrimary" size="md">
            Start Free Trial
          </NewButton>
        </Link>
      )}
    </div>
  );
}

export function OfficeInlineCta({
  eyebrow,
  description,
  primaryText,
  primaryHref,
  secondaryText,
  secondaryHref,
  secondaryExternal = false,
  variant = "emerald",
  layout = "strip",
  className = "",
}: {
  eyebrow: string;
  description?: React.ReactNode;
  primaryText: string;
  primaryHref: string;
  secondaryText: string;
  secondaryHref: string;
  secondaryExternal?: boolean;
  variant?: "emerald" | "gray" | "white";
  layout?: "strip" | "card" | "banner";
  className?: string;
}) {
  const isAdLanding = useIsAdLanding();

  const primaryLinkProps = {
    href: primaryHref,
    target: primaryHref.startsWith("http") ? ("_blank" as const) : ("_self" as const),
    rel: primaryHref.startsWith("http") ? "noopener noreferrer" : undefined,
  };
  const secondaryLinkProps = {
    href: secondaryHref,
    target:
      secondaryExternal || secondaryHref.startsWith("http")
        ? ("_blank" as const)
        : ("_self" as const),
    rel: secondaryExternal || secondaryHref.startsWith("http") ? "noopener noreferrer" : undefined,
  };

  // Ad sessions: hide free-trial primary, keep demo CTA only
  const actionButtons = isAdLanding ? (
    <Link {...secondaryLinkProps}>
      <NewButton variant="primary" size="md">
        {secondaryText}
      </NewButton>
    </Link>
  ) : (
    <>
      <Link {...primaryLinkProps}>
        <NewButton
          variant="primary"
          size="md"
          arrow={primaryHref.startsWith("http") ? "grow" : undefined}
        >
          {primaryText}
        </NewButton>
      </Link>
      <Link {...secondaryLinkProps}>
        <NewButton variant="outlinePrimary" size="md">
          {secondaryText}
        </NewButton>
      </Link>
    </>
  );

  if (layout === "card") {
    return (
      <div
        className={`max-w-4xl mx-auto rounded-2xl border border-gray-200 px-6 py-8 md:px-10 md:py-9 text-center shadow-sm ${inlineCtaBg[variant]} ${className}`}
        aria-label="Call to action"
      >
        {eyebrow ? (
          <p className="mb-5 text-base font-semibold text-text-primary md:text-lg">{eyebrow}</p>
        ) : null}
        <div className="flex flex-wrap gap-3 justify-center">{actionButtons}</div>
      </div>
    );
  }

  if (layout === "banner") {
    return (
      <div
        className={`flex flex-col items-start justify-between gap-4 rounded-xl border border-gray-200/60 bg-gray-100 p-6 md:flex-row md:items-center md:gap-8 md:p-8 ${className}`}
        aria-label="Call to action"
      >
        <div className="min-w-0">
          {eyebrow ? <h3 className="font-semibold text-text-primary mb-1.5">{eyebrow}</h3> : null}
          {description ? <div className="text-sm text-text-secondary">{description}</div> : null}
        </div>
        <div className="flex flex-wrap gap-3 shrink-0">{actionButtons}</div>
      </div>
    );
  }

  return (
    <div
      className={`border-l-4 border-[#1D9E75] rounded-r-lg px-6 py-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between flex-wrap shadow-sm ${inlineCtaBg[variant]} ${className}`}
      aria-label="Call to action"
    >
      {eyebrow ? <p className="text-sm font-semibold text-[#085041] m-0">{eyebrow}</p> : null}
      <div className="flex flex-wrap gap-3">{actionButtons}</div>
    </div>
  );
}
